import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { StatusBadge, fullAddress } from "@/lib/cases";
import { AppShell } from "@/components/app/AppShell";
import { Box, VerdictBadge } from "@/components/app/CaseWork";
import { openFile } from "@/pages/Ops";
import { Input } from "@/components/ui/input";

const btn = "rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[10px] font-semibold text-white disabled:opacity-50";

const ConsentBox = ({ c, reload }) => {
  const [name, setName] = useState("");
  const [field, setField] = useState(false);
  const send = async (decision) => {
    try {
      await api.post(`/me/cases/${c.id}/consent`, { decision, signature_name: name, agree_field: field });
      toast.success(decision === "sign" ? "Consent recorded" : "Request declined");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div className="mt-3 grid gap-3" data-testid={`me-consent-${c.id}`}>
      <p className="whitespace-pre-line rounded-xl bg-oath-bg/70 p-3 text-xs leading-relaxed text-oath-secondary">{c.consent_text}</p>
      {c.check_type === "records_field" && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={field} onChange={(e) => setField(e.target.checked)} data-testid={`me-consent-field-${c.id}`} /> I also authorize the exterior field visit</label>}
      <Input placeholder="Type your full name to sign" value={name} onChange={(e) => setName(e.target.value)} data-testid={`me-consent-name-${c.id}`} />
      <div className="flex gap-2">
        <button className={btn} disabled={name.trim().length < 3} onClick={() => send("sign")} data-testid={`me-consent-sign-${c.id}`}>SIGN CONSENT</button>
        <button className="rounded-full border border-gray-300 px-5 py-2.5 text-xs" onClick={() => send("decline")} data-testid={`me-consent-decline-${c.id}`}>Decline</button>
      </div>
    </div>
  );
};

const Upload = ({ c, types, reload }) => {
  const [type, setType] = useState("utility_bill");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries({ file, doc_type: type, doc_date: date, case_id: c.id }).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await api.post("/me/documents", fd);
      toast.success("Document uploaded for review");
      setFile(null);
      e.target.reset();
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="mt-3 grid gap-2 sm:grid-cols-[1fr_160px]" data-testid={`me-upload-${c.id}`}>
      <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid={`me-upload-type-${c.id}`}>
        {Object.entries(types).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} title="Date on the document" data-testid={`me-upload-date-${c.id}`} />
      <input type="file" accept="image/*,application/pdf" capture="environment" required onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid={`me-upload-file-${c.id}`} />
      <button disabled={busy} className={btn} data-testid={`me-upload-submit-${c.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPLOAD"}</button>
      <p className="text-xs text-oath-muted sm:col-span-2">Photo or PDF, up to 10 MB. Bills and statements should be dated within the last 90 days. You may cover account numbers.</p>
    </form>
  );
};

export default function MyAccount() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/me/cases").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <AppShell eyebrow="— YOUR ACCOUNT" title="Verification requests about you">
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="grid gap-4" data-testid="me-cases">
          {d.needs_verification && <Box title="CONFIRM YOUR EMAIL" testid="me-needs-verification"><p className="text-sm text-oath-secondary">We sent a confirmation link to your email. Open it to see verification requests about you.</p><button onClick={() => api.post("/me/resend-verification").then(() => toast.success("Confirmation email sent")).catch((e) => toast.error(formatApiError(e)))} className="mt-3 text-sm text-oath-accent underline" data-testid="me-resend-verification">Resend confirmation email</button></Box>}
          {!d.needs_verification && d.cases.length === 0 && <Box title="NO REQUESTS YET" testid="me-empty"><p className="text-sm text-oath-secondary">When a business asks us to verify your address using the email, date of birth and phone on this account, the request appears here.</p></Box>}
          {d.cases.map((c) => {
            const docs = d.documents.filter((f) => f.case_id === c.id);
            return (
              <Box key={c.id} title={`${c.org_name.toUpperCase()} · ${c.case_number}`} testid={`me-case-${c.id}`}>
                <div className="flex flex-wrap items-center gap-2"><StatusBadge status={c.status} testid={`me-case-status-${c.id}`} />{c.verdict && <VerdictBadge v={c.verdict.verdict} />}</div>
                <p className="mt-2 text-sm text-oath-ink">{fullAddress(c.address)}</p>
                <p className="text-xs text-oath-secondary">Purpose: {c.purpose} · Requested {fmtDate(c.created_at)}</p>
                {c.verdict && <p className="mt-2 text-sm">{c.verdict.summary} <a href="/dispute" className="text-oath-accent underline">Dispute this result</a></p>}
                {c.status === "awaiting_consent" && <ConsentBox c={c} reload={load} />}
                {["consent_given", "in_progress", "qa_review"].includes(c.status) && <Upload c={c} types={d.doc_types} reload={load} />}
                {docs.length > 0 && (
                  <ul className="mt-3 grid gap-1 text-xs" data-testid={`me-docs-${c.id}`}>
                    {docs.map((f) => <li key={f.id}><button onClick={() => openFile(f.id)} className="text-oath-accent underline">{d.doc_types[f.doc_type]}</button> · {f.review_status}{f.review_reason ? ` (${f.review_reason})` : ""}{f.archived ? " · archived" : ""}</li>)}
                  </ul>
                )}
              </Box>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
